import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Collapse,
    IconButton,
    IconButtonProps,
    Link,
    Paper,
    TableContainer,
    Typography,
    styled,
    Tooltip,
    ClickAwayListener
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Zoom from "@mui/material/Zoom";

import dynamic from "next/dynamic";

import React, { useRef } from "react";

import { PortableText } from "@portabletext/react";
import { PortableTextComponents, PortableTextProps } from "@portabletext/react/src/types";

import Image from "../../../../components/Atoms/SanityImage";
import { CodeType, CustomTableType, TableType } from "../../../../lib/sanity/types";

const Map = dynamic(() => import("../../../../components/Atoms/GPXRoute"), {
    ssr: false
});
const SyntaxHighlighter = dynamic(
    () => import("../../../../components/Atoms/CodePreview")
);

type Props = {
    value: PortableTextProps["value"];
};

const Pre = ({ children }: { children: JSX.Element }) => (
    <Typography variant="pre">{children}</Typography>
);

const copyWidth = {
    maxWidth: "760px",
    marginLeft: "auto",
    marginRight: "auto"
};

const copyWidthWide = {
    ...copyWidth,
    maxWidth: "840px"
};

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { ...other } = props;
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest
    })
}));

const portableTextComponents: PortableTextComponents = {
    list: {
        // https://www.sanity.io/docs/portable-text-to-react#list
        bullet: ({ children }) => <Typography variant="ul">{children}</Typography>,
        number: ({ children }) => <Typography variant="ol">{children}</Typography>
    },
    listItem: {
        // https://www.sanity.io/docs/portable-text-to-react#listitem
        bullet: ({ children }) => <Typography variant="li">{children}</Typography>
    },
    block: {
        normal: React.memo((props) => <Typography>{props.children}</Typography>),
        h2: React.memo((props) => (
            <Typography variant="copyH2" gutterBottom>
                {props.children}
            </Typography>
        )),
        h3: React.memo((props) => (
            <Typography variant="copyH3" gutterBottom>
                {props.children}
            </Typography>
        )),
        h4: React.memo((props) => (
            <Typography variant="copyH4" gutterBottom>
                {props.children}
            </Typography>
        )),
        h5: React.memo((props) => (
            <Typography variant="copyH5" gutterBottom>
                {props.children}
            </Typography>
        )),
        h6: React.memo((props) => (
            <Typography variant="copyH6" gutterBottom>
                {props.children}
            </Typography>
        )),
        blockquote: React.memo((props) => (
            <Typography variant="blockquote">{props.children}</Typography>
        ))
    },
    types: {
        image: React.memo((props) => (
            <Image
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                sx={{
                    "& img": {
                        display: "block",
                        margin: "1em auto 2em",
                        backgroundColor: "#f4f4f4",
                        ...copyWidthWide
                    }
                }}
            />
        )),
        customTable: React.memo((props: { value: CustomTableType }) => {
            const [headRow, ...rest] = props.value.data.rows;

            const rows = props.value.firstRowIsHeader ? rest : props.value.data.rows;
            return (
                <TableContainer component={Paper} sx={{ ...copyWidth, mb: 3 }}>
                    <Table>
                        {props.value.firstRowIsHeader && (
                            <TableHead>
                                <TableRow sx={{ fontWeight: 700 }}>
                                    {headRow.cells.map((cell, index) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <TableCell key={index}>{cell}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                        )}
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow
                                    // eslint-disable-next-line no-underscore-dangle
                                    key={row._key}
                                    sx={{
                                        "&:last-child td, &:last-child th": { border: 0 }
                                    }}
                                >
                                    {row.cells.map((cell, index) => (
                                        <TableCell
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            component={
                                                props.value.firstColumnIsHeader &&
                                                index === 0
                                                    ? "th"
                                                    : "td"
                                            }
                                        >
                                            {cell}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }),
        table: React.memo((props: { value: TableType }) => (
            <TableContainer component={Paper} sx={{ ...copyWidth, mb: 3 }}>
                <Table>
                    <TableBody>
                        {props.value.rows.map((row) => (
                            <TableRow
                                // eslint-disable-next-line no-underscore-dangle
                                key={row._key}
                                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                            >
                                {row.cells.map((cell, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <TableCell key={index}>{cell}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )),
        route: React.memo((props) => (
            <Map routeData={props.value.geojson} displayElevation />
        )),
        note: React.memo((props) => {
            const [expanded, setExpanded] = React.useState(props.value.startOpen);
            const handleExpandClick = () => setExpanded(!expanded);
            return (
                <Box component="section" sx={copyWidth}>
                    <Card
                        sx={{
                            marginBottom: "1em",
                            backgroundColor: "#222",
                            color: "#f5f5f5",
                            borderRadius: 5
                        }}
                        variant="outlined"
                    >
                        <CardHeader
                            title={props.value.title}
                            titleTypographyProps={{
                                variant: "h4",
                                color: "#f5f5f5",
                                mb: 0,
                                borderRadius: 5
                            }}
                            action={
                                <ExpandMore
                                    expand={expanded}
                                    onClick={handleExpandClick}
                                    aria-expanded={expanded}
                                    aria-label="show more"
                                    sx={{
                                        color: "#f5f5f5"
                                    }}
                                >
                                    <ExpandMoreIcon />
                                </ExpandMore>
                            }
                            // onClick={() => setExpanded(!expanded)}
                        />
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <CardContent>
                                <PortableText
                                    value={props.value.body}
                                    components={portableTextComponents}
                                />
                            </CardContent>
                        </Collapse>
                    </Card>
                </Box>
            );
        }),
        code: React.memo((props: { value: CodeType }) => (
            <SyntaxHighlighter
                codeTagProps={{
                    className: `hljs hljs-${props.value.language}`
                }}
                PreTag={Pre}
                language={props.value.language}
                showLineNumbers
                useInlineStyles={false}
            >
                {props.value.code}
            </SyntaxHighlighter>
        ))
    },
    marks: {
        link: React.memo((props) => (
            <Link href={props.value.href}>{props.children}</Link>
        )),
        infoPopup: React.memo((props) => {
            const [open, setOpen] = React.useState(false);
            const iconRef = useRef(null);

            const handleTooltipClose = () => {
                setOpen(false);
            };

            const handleTooltipOpen = () => {
                setOpen(true);
            };

            return (
                <Box component="span">
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                        <Tooltip
                            TransitionComponent={Zoom}
                            arrow
                            title={<Box>{props.value.text}</Box>}
                            onClose={handleTooltipClose}
                            open={open}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            PopperProps={{
                                anchorEl: iconRef.current
                            }}
                        >
                            <Box
                                onClick={handleTooltipOpen}
                                component="span"
                                sx={{
                                    textDecoration: "underline",
                                    textDecorationStyle: "dotted"
                                }}
                            >
                                <>
                                    <InfoIcon
                                        ref={iconRef}
                                        sx={{
                                            position: "relative",
                                            top: "6px",
                                            marginRight: 1
                                        }}
                                    />
                                    {props.children}
                                </>
                            </Box>
                        </Tooltip>
                    </ClickAwayListener>
                </Box>
            );
        })
    }
};

const Render = ({ value }: Props) => (
    <PortableText value={value} components={portableTextComponents} />
);

export default Render;
